/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import _isEmpty from 'lodash/isEmpty';

// Components
import Button from '../Button';

// Utils
import { formatBytes } from '../../utils/formatters';
import { validateAttrType } from '../../utils/validators';

// Icons
import CameraPlusIcon from '../../icons/camera-plus.svg';
import DeleteIcon from '../../icons/delete.svg';

// Styles
import styles from './ImageLoader.styl';

const cx = classNames.bind(styles);

function ImageLoader(props) {
  const {
    className,
    allowedExtensions,
    maxFileSize,
    maxFilesAmount,
    onLoadFiles,
    onRemoveFile,
    caption,
    intl,
  } = props;

  const [errors, setErrors] = useState([]);
  const [gallery, setGallery] = useState([]);

  const getErrorMessages = rejectedFiles => {
    const newErrors = [];

    rejectedFiles.forEach(file => {
      if (!validateAttrType(file, allowedExtensions)) {
        newErrors.push(`Неверный тип файла ${file.name}`);
      }

      if (file.size >= maxFileSize) {
        newErrors.push(`Файл ${file.name} слишком большой`);
      }
    });

    return newErrors;
  };

  const handleOnDrop = (acceptedFiles, rejectedFiles) => {
    const slicedAcceptedFiles =
      gallery.length + acceptedFiles.length > maxFilesAmount
        ? acceptedFiles.slice(0, maxFilesAmount - gallery.length)
        : acceptedFiles;

    onLoadFiles(slicedAcceptedFiles);

    const receivedFiles = slicedAcceptedFiles.map(f => ({
      ...f,
      preview: URL.createObjectURL(f),
      name: f.name,
      size: f.size,
    }));

    const newGallery = [...gallery, ...receivedFiles];
    setGallery(newGallery);

    setErrors(rejectedFiles.length > 0 ? getErrorMessages(rejectedFiles) : []);
  };

  const removeImageFromGallery = imageIndex => {
    const newGallery = gallery.filter((image, key) => key !== imageIndex);

    setGallery(newGallery);
    onRemoveFile(imageIndex);
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop: handleOnDrop,
    accept: allowedExtensions,
    maxSize: maxFileSize,
    multiple: maxFilesAmount > 1,
  });

  useEffect(() => {
    return () => {
      gallery.forEach(file => URL.revokeObjectURL(file.preview));
    };
  }, [gallery]);

  return (
    <div className={cx('ImageLoader', className)}>
      <div className={cx('ImageLoader__workarea')}>
        {gallery.map((image, index) => (
          <figure className={cx('ImageLoader__image')} key={image.preview}>
            <div
              className={cx('ImageLoader__image-preview')}
              style={{ backgroundImage: `url('${image.preview}')` }}
            />
            <figcaption className={cx('ImageLoader__image-caption')}>
              <span>{formatBytes(image.size, 2)}</span>
              <Button
                className={cx('ImageLoader__image-delete')}
                onClick={() => removeImageFromGallery(index)}
                link
              >
                <DeleteIcon width={16} height={16} />
              </Button>
            </figcaption>
          </figure>
        ))}
        {gallery.length < maxFilesAmount && (
          <div
            {...getRootProps({
              className: cx('ImageLoader__dropzone', {
                ImageLoader__dropzone_active: isDragActive,
                ImageLoader__dropzone_rejected: isDragReject,
              }),
            })}
          >
            <input {...getInputProps()} />
            <CameraPlusIcon width={32} height={32} className={cx('ImageLoader__icon')} />
            <div className={cx('ImageLoader__caption')}>
              {caption || intl.formatMessage({ id: 'field.addPhoto.label' })}
            </div>
          </div>
        )}
      </div>
      {!_isEmpty(errors) && (
        <div>
          {errors.map((error, index) => (
            <div className={cx('ImageLoader__error')} key={index}>
              {error}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default injectIntl(ImageLoader);

ImageLoader.propTypes = {
  className: PropTypes.string,
  allowedExtensions: PropTypes.string,
  maxFileSize: PropTypes.number,
  maxFilesAmount: PropTypes.number,
  onLoadFiles: PropTypes.func,
  onRemoveFile: PropTypes.func,
  caption: PropTypes.string,
  intl: PropTypes.object.isRequired,
};

ImageLoader.defaultProps = {
  className: '',
  allowedExtensions: 'image/*',
  maxFileSize: 4194304,
  maxFilesAmount: 1,
  onLoadFiles: () => null,
  onRemoveFile: () => null,
  caption: '',
};
